/* LuxeLane v2 cart drawer + cart page behaviour.
 *
 * Every element with [data-lx-cart-section][data-section-id] is a Shopify
 * section whose [data-lx-cart-swap] region is re-rendered through the
 * Section Rendering API after a change, so markup stays in Liquid.
 *  - quantity +/- / remove / typed quantity -> /cart/change.js
 *  - update icon -> option selects; save = add the chosen variant, then
 *    drop the old line by its key (Shopify can't swap a line's variant)
 *  - "Complete your ritual" Add -> image flies into the bag, then
 *    window.LxCart.addToCart
 *  - after every re-render the free-shipping bar animates from its old
 *    value and newly arrived lines slide in
 *  - lx:cart-updated (any add on the site) -> refresh, then open the drawer
 *    (not on the cart page, which already shows the bag)
 * Drawer: opens from [data-lx-cart-open], closes on Escape / overlay /
 * close button, traps focus and pauses Lenis while open.
 */
(() => {
  const ENTER_MS = 700;
  const FLY_MS = 800;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const routeRoot = () => window.Shopify?.routes?.root || '/';
  const drawer = () => document.querySelector('[data-lx-drawer]');
  const onCartPage = () => Boolean(document.querySelector('[data-lx-cart-page]'));
  const sectionEls = () => [...document.querySelectorAll('[data-lx-cart-section][data-section-id]')];
  const sectionIds = () => sectionEls().map((el) => el.dataset.sectionId);

  /* ---------- Rendering ---------- */
  const barScale = (bar) => Number(/scaleX\(([\d.]+)\)/.exec(bar?.style.transform || '')?.[1] || 0);

  function swapSections(sections) {
    if (!sections) return;
    const oldKeys = new Set([...document.querySelectorAll('[data-lx-cart-swap] [data-key]')].map((row) => row.dataset.key));
    sectionEls().forEach((el) => {
      const html = sections[el.dataset.sectionId];
      if (!html) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-lx-cart-swap]');
      const current = el.querySelector('[data-lx-cart-swap]');
      if (!fresh || !current) return;
      const fromScale = barScale(current.querySelector('.lx-ship__track i'));
      const wasDone = Boolean(current.querySelector('[data-lx-ship].is-done'));
      current.replaceWith(fresh);
      // free shipping just unlocked: one-off celebration
      const ship = fresh.querySelector('[data-lx-ship]');
      if (ship && !wasDone && ship.classList.contains('is-done') && current.querySelector('[data-lx-ship]')) {
        ship.classList.add('is-celebrate');
      }

      // free-shipping bar: start at the old fill, then ease to the new one
      const bar = fresh.querySelector('.lx-ship__track i');
      if (bar && !reduceMotion) {
        const target = bar.style.transform;
        bar.style.transition = 'none';
        bar.style.transform = `scaleX(${fromScale})`;
        bar.getBoundingClientRect();
        bar.style.transition = '';
        requestAnimationFrame(() => (bar.style.transform = target));
      }
      if (oldKeys.size) {
        fresh.querySelectorAll('[data-key]').forEach((row) => {
          if (!oldKeys.has(row.dataset.key)) row.classList.add('is-new');
        });
      }
    });
  }

  async function postCart(path, body) {
    const res = await fetch(`${routeRoot()}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Could not update your bag.');
    return data;
  }

  // Loading feedback: the whole bag gets a progress line and faded totals,
  // the line being changed gets a spinner. Re-rendering clears the row state.
  function setBusy(busy, row = null) {
    document.querySelectorAll('[data-lx-cart-swap]').forEach((el) => {
      el.classList.toggle('is-busy', busy);
      el.setAttribute('aria-busy', String(busy));
    });
    if (row && row.isConnected) row.classList.toggle('is-loading', busy);
  }

  function showError(message) {
    document.querySelectorAll('[data-lx-cart-error]').forEach((el) => {
      el.textContent = message;
      el.hidden = !message;
    });
  }

  async function refresh() {
    const ids = sectionIds();
    if (!ids.length) return;
    try {
      const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      if (!res.ok) throw new Error(`Cart refresh failed (${res.status})`);
      swapSections(await res.json());
    } catch (_) {
      /* keep the rendered bag; the next change will re-render it */
    }
  }

  async function changeLine(line, quantity, row = null) {
    setBusy(true, row);
    showError('');
    try {
      const data = await postCart('cart/change.js', {
        line: Number(line),
        quantity: Math.max(0, Number(quantity) || 0),
        sections: sectionIds(),
      });
      swapSections(data.sections);
      window.LxCart?.setCount(data.item_count);
    } catch (error) {
      await refresh();
      showError(error.message);
    } finally {
      setBusy(false, row);
    }
  }

  /* ---------- "Remove this item?" confirmation (native dialog: focus
     trap, Escape and top layer for free; works above the drawer) ---------- */
  function confirmRemove(name) {
    let dialog = document.getElementById('LxConfirm');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'LxConfirm';
      dialog.className = 'lx-confirm';
      dialog.setAttribute('aria-labelledby', 'LxConfirmTitle');
      dialog.innerHTML = `
        <form method="dialog">
          <p class="lx-confirm__title" id="LxConfirmTitle">Remove item?</p>
          <p class="lx-confirm__text" data-lx-confirm-text></p>
          <div class="lx-confirm__actions">
            <button value="cancel" class="lx-confirm__cancel">Cancel</button>
            <button value="ok" class="lx-btn-dark"><i></i><span>Remove</span></button>
          </div>
        </form>`;
      // click on the dimmed backdrop = cancel
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close('cancel');
      });
      document.body.append(dialog);
    }
    dialog.querySelector('[data-lx-confirm-text]').textContent = `Are you sure you want to remove ${name} from your bag?`;
    dialog.returnValue = '';
    return new Promise((resolve) => {
      dialog.addEventListener('close', () => resolve(dialog.returnValue === 'ok'), { once: true });
      dialog.showModal();
    });
  }

  /* ---------- Line controls (delegated: markup is replaced on every change) ---------- */
  document.addEventListener('click', (event) => {
    const control = event.target.closest('[data-lx-cart-section] [data-lx-line][data-lx-qty]');
    if (!control) return;
    event.preventDefault();
    const quantity = Number(control.dataset.lxQty);
    const row = control.closest('.lx-line');
    if (quantity === 0 && row) {
      const name = row.querySelector('.lx-line__title')?.textContent.trim() || 'this item';
      confirmRemove(name).then((ok) => {
        if (ok) changeLine(control.dataset.lxLine, 0, row);
      });
    } else {
      changeLine(control.dataset.lxLine, quantity, row);
    }
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest('[data-lx-cart-section] [data-lx-line-input]');
    if (input) changeLine(input.dataset.lxLineInput, input.value, input.closest('.lx-line'));
  });

  // Enter in a quantity field updates that line instead of submitting the page form.
  document.addEventListener('keydown', (event) => {
    const input = event.target.closest('[data-lx-cart-section] [data-lx-line-input]');
    if (!input || event.key !== 'Enter') return;
    event.preventDefault();
    input.blur();
  });

  /* ---------- Change a line's variant ---------- */
  function togglePanel(row, open) {
    const panel = row.querySelector('[data-lx-line-panel]');
    const btn = row.querySelector('[data-lx-line-edit]');
    if (!panel || !btn) return;
    panel.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    if (open) panel.querySelector('select')?.focus({ preventScroll: true });
    else btn.focus({ preventScroll: true });
  }

  async function saveVariant(row) {
    const panel = row.querySelector('[data-lx-line-panel]');
    const variants = JSON.parse(panel.querySelector('[data-lx-line-variants]')?.textContent || '[]');
    const values = [...panel.querySelectorAll('[data-lx-opt]')].map((select) => select.value);
    const match = variants.find((v) => v.options.every((opt, i) => opt === values[i]));
    showError('');
    if (!match) return showError("That combination isn't available.");
    if (match.id === Number(row.dataset.variantId)) return togglePanel(row, false);
    if (!match.available) return showError('That option is sold out.');

    setBusy(true, row);
    try {
      const item = { id: match.id, quantity: Number(row.dataset.qty) || 1 };
      if (row.dataset.plan) item.selling_plan = Number(row.dataset.plan);
      await postCart('cart/add.js', { items: [item] });
      const data = await postCart('cart/change.js', { id: row.dataset.key, quantity: 0, sections: sectionIds() });
      swapSections(data.sections);
      window.LxCart?.setCount(data.item_count);
    } catch (error) {
      await refresh();
      showError(error.message);
    } finally {
      setBusy(false, row);
    }
  }

  document.addEventListener('click', (event) => {
    const row = event.target.closest('[data-lx-cart-section] .lx-line');
    if (!row) return;
    if (event.target.closest('[data-lx-line-edit]')) {
      togglePanel(row, row.querySelector('[data-lx-line-panel]')?.hidden !== false);
    } else if (event.target.closest('[data-lx-line-cancel]')) {
      togglePanel(row, false);
    } else if (event.target.closest('[data-lx-line-save]')) {
      saveVariant(row);
    }
  });

  /* ---------- Suggestions: fly into the bag, then add ---------- */
  function flyToBag(img, section) {
    if (!img || reduceMotion) return;
    const target = section?.querySelector('.lx-line .lx-line__thumb') || section?.querySelector('[data-lx-cart-swap]');
    if (!target) return;
    const from = img.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    const ghost = img.cloneNode();
    ghost.removeAttribute('loading');
    ghost.className = 'lx-fly';
    Object.assign(ghost.style, { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px` });
    document.body.append(ghost);
    const dx = to.left + to.width / 2 - (from.left + from.width / 2);
    const dy = to.top + Math.min(to.height, 120) / 2 - (from.top + from.height / 2);
    const scale = Math.max(0.6, Math.min(1.4, Math.min(to.width, 110) / from.width));
    ghost
      .animate(
        [
          { transform: 'translate(0, 0) scale(1)', opacity: 1 },
          { transform: `translate(${dx * 0.45}px, ${dy * 0.45 - 70}px) scale(${(1 + scale) / 2})`, opacity: 1, offset: 0.5 },
          { transform: `translate(${dx}px, ${dy}px) scale(${scale})`, opacity: 0 },
        ],
        { duration: FLY_MS, easing: 'cubic-bezier(0.5, 0, 0.2, 1)' }
      )
      .finished.catch(() => {})
      .finally(() => ghost.remove());
  }

  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-lx-upsell-add]');
    if (!btn || !window.LxCart) return;
    btn.disabled = true;
    showError('');
    flyToBag(btn.closest('.lx-up')?.querySelector('.lx-up__img img'), btn.closest('[data-lx-cart-section]'));
    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    try {
      await window.LxCart.addToCart(btn.dataset.lxUpsellAdd, 1); // fires lx:cart-updated -> refresh
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      btn.textContent = 'Added';
    } catch (error) {
      btn.classList.remove('is-loading');
      btn.textContent = 'Add';
      btn.disabled = false;
      showError(error.message);
    } finally {
      btn.removeAttribute('aria-busy');
    }
  });

  /* ---------- Drawer ---------- */
  let lastFocus = null;

  function openDrawer() {
    const root = drawer();
    if (!root || root.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    root.classList.add('is-open', 'is-entering');
    setTimeout(() => root.classList.remove('is-entering'), ENTER_MS);
    if (window.LxLenis) window.LxLenis.stop();
    else document.documentElement.style.overflow = 'hidden';
    root.querySelector('.lx-cd__panel')?.focus({ preventScroll: true });
  }

  function closeDrawer() {
    const root = drawer();
    if (!root || !root.classList.contains('is-open')) return;
    root.classList.remove('is-open', 'is-entering');
    if (window.LxLenis) window.LxLenis.start();
    else document.documentElement.style.overflow = '';
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-lx-drawer-close]')) {
      closeDrawer();
      return;
    }
    const opener = event.target.closest('[data-lx-cart-open]');
    if (!opener || !drawer() || onCartPage()) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey) return; // let "open in new tab" work
    event.preventDefault();
    openDrawer();
  });

  document.addEventListener('keydown', (event) => {
    const root = drawer();
    if (!root?.classList.contains('is-open')) return;
    if (document.querySelector('dialog[open]')) return; // the confirm dialog handles its own keys
    if (event.key === 'Escape') {
      closeDrawer();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* ---------- Adds from anywhere (product cards, PDP, suggestions) ---------- */
  document.addEventListener('lx:cart-updated', async (event) => {
    // product cards on desktop pass openDrawer: false (the badge is the feedback)
    if (!onCartPage() && event.detail?.openDrawer !== false) openDrawer();
    setBusy(true);
    await refresh();
    setBusy(false);
  });

  // Back/forward cache can restore a stale bag.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      refresh();
      window.LxCart?.refreshCartCount();
    }
  });
})();
